<template> 
  <v-card flat :height="height">
      <v-layout row>
        <v-flex xs12 md6 lg6 justify-self-center pa-2>
          <v-combobox
            v-model.trim="activeGroupSet"
            :filter="comboFilter"
            :items="sectionGroupSets"
            :search-input.sync="search"
            flat
            label="Select Group Set"
            solo
            hide-details
          >
            <!-- Nothing returned from filter -->
            <template v-slot:no-data>
              <v-list-tile>
                <v-flex xs8 class="subheading text-truncate" >Create: {{ search }}</v-flex>
                <v-spacer></v-spacer>
                <v-flex xs4 v-if="$v.search.$invalid && !$v.search.maxLength" class="error--text text-xs-right">Name cannot exceed 255 characters.</v-flex>
                <v-flex xs4 v-if="$v.search.$invalid && !$v.search.required"  class="error--text text-xs-right">Name is required.</v-flex>
              </v-list-tile>
            </template>
            <!-- active/selected group set-->
            <template v-slot:selection="{ item, parent, selected }">
              <span v-if="item === Object(item)"  >
                <span class="pr-2">{{ item.name }}</span>
                <v-icon small @click="activeGroupSet = null" color="primary">close</v-icon>
              </span>
            </template>
            <!-- groupsets select rows returned from filter -->
            <template v-slot:item="{ item }">
              <v-list-tile-content>{{ item.name }}</v-list-tile-content>
              <v-spacer></v-spacer>
              <img v-if="item.lti" class="lms-img-sm" :src="item.lmsImg">
            </template>
          </v-combobox>
        </v-flex>
      </v-layout>
      <v-layout justify-center row v-if="activeGroupSet" :style="LTIlaunch && 'height:60vh'">
        <v-layout justify-center row>
          <v-card justify-center row fill-height width="95%" flat color="white" >
            <v-card-title class="pb-1">
              <h2 v-if='activeGroupSet && editing !== activeGroupSet' class="text-truncate" style="width:calc(100% - 80px)">{{ activeGroupSet.name}}</h2>
              <v-text-field
                v-if="editing === activeGroupSet"
                v-model.trim="editing.name"
                :rules="[rules.notEmpty, rules.maxLength255]"
                @keyup.enter="editGroupSet(activeGroupSet)"
                @input="$v.activeGroupSet.name.$touch"
                autofocus
                background-color="transparent"
                counter=255
                flat
                :hide-details="!$v.activeGroupSet.name.$error"
                solo
                ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn v-if="editing" icon  @click.stop.prevent="editGroupSet(activeGroupSet)">
                <v-icon color="primary">check</v-icon>
              </v-btn>
              <img v-if="activeGroupSet.lti" class="lms-img-sm" :src="activeGroupSet.lmsImg">
              <v-menu offset-y left>
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" flat icon v-on="on">
                     <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-tile v-if="!activeGroupSet.lti" @click="editGroupSet(activeGroupSet)">{{ editing ? 'Save' : 'Edit' }}</v-list-tile>
                  <v-list-tile @click="removeGroupSet(activeGroupSet.id)">Delete</v-list-tile>
                </v-list>
              </v-menu>
            </v-card-title>
            <v-spacer></v-spacer>
            <div class="mx-3">
              <v-btn v-if="activeGroupSet" class="mt-2" large block outline color="primary" @click="createGroup" :disabled="_.get(activeGroupSet, 'lti', false)">
                <v-icon left>add_circle_outline</v-icon> Add Group
              </v-btn>
              <span v-if="_.get(activeGroupSet, 'lti', false)">Cannot modify groups imported from an LMS</span>
            </div>
            <v-card-text class="draggable-group" :height="LTIlaunch && 'height: 40vh;'">
              <div v-for="(group, index) in groups">
                <v-hover v-slot:default="{ hover }">
                  <v-text-field
                    class="body-1 groupText text--primary"
                    v-model.trim="group.name"
                    :disabled="_.get(activeGroupSet, 'lti', false)"
                    :rules="[rules.notEmpty, rules.maxLength255]"
                    :hide-details="!$v.groups.$each[index].$error"
                    solo
                    flat
                    :append-icon="group.students.length === 0 && hover ? 'delete' : ''"
                    @input="$v.groups.$each[index].name.$touch()"
                    @blur="editGroup(group)"
                    @keyup.enter="editGroup(group)"
                    @click:append="removeGroup(group.id)"
                    color="primary"
                  >{{ group.name }}
                  </v-text-field>
                </v-hover>
                <draggable 
                  group="students"  
                  :data-group-id="group.id" 
                  :data-group-name="group.name" 
                  :disabled="popoverDataLoading || _.get(activeGroupSet, 'lti', false)"
                  v-model="group.students" 
                  @add="moveStudent" 
                  :sort="false"
                >
                  <!-- DraggableGroupStudent -->
                  <div v-for="student in group.students" :data-student-id="student.id" :data-student-name="student.firstName + ' ' + student.lastName" class="student-outline my-2 elevation-1" :class="{grab: !_.get(activeGroupSet, 'lti', false)}">
                    <v-list-tile
                      :key="student.id"
                      class="pa-0"
                    >
                      <v-list-tile-avatar>
                        <v-icon>perm_identity</v-icon>
                      </v-list-tile-avatar>
                      <v-list-tile-content>
                        <v-list-tile-title>{{ student.firstName }} {{student.lastName}}</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                  </div>
                  <div v-if="group.students.length === 0" class='drag-here'>Drop learner</div>
                </draggable>
              </div>
            </v-card-text>
          </v-card>
        </v-layout>
        <v-layout xs6 justify-center column align-start fill-height class="pa-3">
          <h2 class="pa-2">Unassigned Learners</h2>
          <!-- DraggableGroupStudent -->
          <draggable 
            group="students"
            style="overflow: auto; height: 75vh; width: 100%;"
            @add="removeStudent" 
            :disabled="popoverDataLoading || !activeGroupSet"
            :sort="false"
            v-model="ungroupedStudent"
            :emptyInsertThreshold="100"
            >
            <div v-for="student in ungroupedStudent"  :data-student-id="student.id"  :data-student-name="student.firstName + ' ' + student.lastName" class="student-outline my-2 elevation-1" :class="{grab: !_.get(activeGroupSet, 'lti', false)}">
              <v-list-tile
                :key="student.id"
                class="pa-0"
              >
                <v-list-tile-avatar>
                  <v-icon>perm_identity</v-icon>
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title>{{ student.firstName }} {{student.lastName}}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </div>
            <div v-if="ungroupedStudent.length === 0" class='drag-here'>No learners.
              <span>Read more 
                <a v-if="LMSdata.type === 'Canvas'" href="https://knowledge.playposit.com/article/224-peer-review-instructors#groups" target="_blank">here</a>
                <a v-else-if="LMSdata.type !== 'Canvas'" href="https://knowledge.playposit.com/article/291-peer-review-for-instructors-using-playposit-groups#groups" target="_blank">here</a>
                <a v-else href="https://knowledge.playposit.com/article/277-peer-review-without-an-lms#groups" target="_blank">here</a>
              </span>
            </div>
          </draggable>
        </v-layout>
      </v-layout>
  </v-card>
</template>

<script>
/* global _ */
/* global moment */
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
import draggable from 'vuedraggable';
import {
  required,
  maxLength
} from 'vuelidate/lib/validators';

export default {
  name: 'GroupEdit',
  components: { 
    draggable
  },
  props: [ 
    'height',
    'sectionId',
  ], 
  data() {
    return {
      activeGroupSet: null,
      editing: null,
      groups: [],
      rules: {
        notEmpty: value => !!value.trim().length || 'Name is required.',
        maxLength255: value => _.get(value, 'length', 0) < 255 || 'Name cannot exceed 255 characters',
      },
      search: '',
      ungroupedStudent: [],
    }
  },
  computed: {
    ...mapGetters([
      'popoverDataLoading',
      'LMSdata',
      'LTIdata',
      'LTIlaunch',
    ]),
    ...mapGetters('instructors', [
      'groupSets',
      'students',
    ]),
    sectionGroupSets() {
      // pull groupSets for the specific section and append a header element for the combobox
      const gs = this.groupSets[this.sectionId] || []
      gs.splice(0, 0, { header: 'Type to add or filter'});
      return gs
    }
  },
  methods: {
    ...mapActions('instructors', [
      // groups:
      'getGroups',
      'addGroup',
      'updateGroup',
      'deleteGroup',
      // groupStudents:
      'assignGroupStudent',
      'deleteGroupStudent',
      // groupSets:
      'getGroupSets',
      'addGroupSet',
      'updateGroupSet',
      'deleteGroupSet',
      'getLearners',
    ]),
    ...mapMutations([
      'triggerSnackbar'
    ]),
    // group methods
    async createGroup() {
      // create a new group
      await this.addGroup({ 
        groupSetId: this.activeGroupSet.id, 
        name: `${this.activeGroupSet.name} - Group ${this.groups.length + 1}`,
      });
      await this.getGroupData(this.activeGroupSet.id);
    },
    async getGroupData(groupSetId) {
      // refresh the group data
      this.groups = await this.getGroups(groupSetId);
      this.ungroupedStudent = this.filterGroups()
    },
    async removeGroup(groupId) {
      await this.deleteGroup({
        groupId,
        groupSetId: this.activeGroupSet.id,
      });
      await this.getGroupData(this.activeGroupSet.id);
    },
    async editGroup(group) {
      // change the nane of a group
      const groupValidator = _.find(this.$v.groups.$each, gV => gV.$model.id === group.id)
      if (groupValidator.$dirty && !groupValidator.$error) {
        await this.updateGroup({
          groupId: group.id,
          groupSetId: this.activeGroupSet.id, 
          name: group.name,
        });
        groupValidator.name.$reset();
      }
    },
    // groupStudent methods
    async moveStudent(draggedItem) {
      const groupId = _.get(draggedItem, 'to.dataset.groupId')
      const studentId = _.get(draggedItem, 'clone.dataset.studentId')
      await this.assignGroupStudent({
        groupId, 
        studentId,
      });
    },
    async removeStudent(draggedItem) {
      const groupId = _.get(draggedItem, 'from.dataset.groupId')
      const studentId = _.get(draggedItem, 'clone.dataset.studentId')
      await this.deleteGroupStudent({
        groupId, 
        studentId
      });
    },
    // groupSet methods
    async createGroupSet(name) {
      // validation for groupSetName
      const groupSet = await this.addGroupSet({ name, sectionId: this.sectionId});
      await this.getGroupSets();
      this.activeGroupSet = {
        id: groupSet.id,
        name: groupSet.name,
        lti: false,
      }
    },
    editGroupSet(item) {
      if (!this.editing) {
        this.editing = item
      } else {
        const groupSetValidator = this.$v.activeGroupSet
        if (groupSetValidator.$error) {
          // toast 
          this.triggerSnackbar({ text:"Invalid Group Set Name.", color: 'red', timeout: 6000 });
        } 
        else {
          this.updateGroupSet({
            groupSetId: item.id,
            name: item.name,
          });
          groupSetValidator.name.$reset();
          this.editing = null;
        } 
      }
    },
    async removeGroupSet(groupSetId) {
      const success = await this.deleteGroupSet(groupSetId);
      if (success) {
        this.activeGroupSet = null;
        await this.getGroupSets();
      }
    },
    comboFilter (item, queryText, itemText) {
      if (item.header) return false;
      const hasValue = val => val != null ? val : ''
      const query = hasValue(queryText)
      return item.name.toString().toLowerCase().indexOf(query.toString().toLowerCase()) > -1
    },
    filterGroups() {
      // build array of student ids already in groups
      const assignedStudents = this.groups.map((group) => {
        if (!_.has(group, 'students')) return []
        return group.students.map(student => student.id)
      }).flat();
      // build array of students not yet in groups
      const unassignedStudents = this.students.data.reduce((unassigned, student) => {
        if (!(assignedStudents.includes(student.id))) {
          unassigned.push(student)
        }
        return unassigned;
      }, []);
      return _.sortBy(unassignedStudents);
    },
  },
  validations: {
    activeGroupSet: { // groupset validations
      name: {
        required,
        maxLength: maxLength(255),
      }
    },
    groups: { // should be group validations
      $each: {
        name: {
          required,
          maxLength: maxLength(255),
        }
      }
    }, 
    search: {
      required, 
      maxLength: maxLength(255),
    }
  },
  watch: {
    'activeGroupSet' (val, prev) {
      if (typeof(val) === 'string') {
        // new name from combo box, create a new groupset and then reload
        const existingGroupSet = _.find(this.sectionGroupSets, gs => gs.name && gs.name.toLowerCase() === val.toLowerCase());
        if (existingGroupSet) {
          this.activeGroupSet = existingGroupSet
        }
        else if (required(val) && val.trim().length <= 255) {
          this.createGroupSet(val.trim());
        } else {
          this.triggerSnackbar({ text:"Invalid Group Set Name.", color: 'red', timeout: 6000 });
        }
      }
      else if (val && typeof(val) === 'object') {
        // item from combo box already exists, load groups
        this.getGroupData(val.id);
      }
    },
  },
  async mounted() { 
    await this.getGroupSets();
    await this.getLearners(this.sectionId);
    this.ungroupedStudent = this.filterGroups();
  },
};
</script>

<style>
  .student-outline {
    border: 1px solid rgba(0,0,0,.54);
    border-radius: 3px;
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-user-select: none; /* Safari */
    -khtml-user-select: none; /* Konqueror HTML */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* Internet Explorer/Edge */
    user-select: none; /* Non-prefixed version, currently */

  }
  .grab {
    cursor: grab !important;
  }
  
  .draggable-groups-container {
    overflow-y: hidden;
    max-height: calc(100vh - 250px);
  }
  .draggable-groups-container .v-card {
    max-height: calc(100vh - 65px)
  }
  
  .draggable-group {
    overflow-y: auto;
    max-height: calc(100vh - 300px)
  }
  .draggable-unassigned-students {
    overflow-y: auto;
    height: calc(100vh - 240px);
  }
  
  .drag-here {
    /*background: ;*/
    border: dashed 1px rgba(0, 0, 0, .87);
    border-radius: 3px;
    padding: 12px 8px;
    text-align: center;
  }
</style>
